import { useState, useRef, useCallback } from 'react';
import { Packer } from 'docx';
import serializeToDOCX from '../utils/serializeToDOCX';

const RIPT_FILE_TYPE = {
  description: 'Ript Transcript',
  accept: { 'application/json': ['.ript'] },
};

const TEXT_FILE_TYPE = {
  description: 'Text Files',
  accept: { 'text/plain': ['.txt'] },
};

const DOCX_FILE_TYPE = {
  description: 'Word Documents',
  accept: { 'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'] },
};

const supportsFileSystemAccess =
  'showOpenFilePicker' in window && 'showSaveFilePicker' in window;

export default function useFileSystem() {
  const [fileName, setFileName] = useState('Untitled');
  const [isModified, setIsModified] = useState(false);
  const fileHandleRef = useRef(null);

  const markModified = useCallback(() => {
    setIsModified(true);
  }, []);

  const markSaved = useCallback(() => {
    setIsModified(false);
  }, []);

  // Save using File System Access API
  const saveWithFSA = async (handle, data) => {
    const writable = await handle.createWritable();
    await writable.write(JSON.stringify(data, null, 2));
    await writable.close();
  };

  // Fallback save via blob download
  const saveWithDownload = (data, name) => {
    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
  };

  const buildRiptData = (editorJson, mode, multilingual) => ({
    version: 1,
    content: editorJson,
    metadata: {
      modified: new Date().toISOString(),
      mode: mode === 'audible' ? 'audible' : 'multimodal',
      multilingual: !!multilingual,
    },
  });

  // --- Public API ---

  const newFile = useCallback(() => {
    fileHandleRef.current = null;
    setFileName('Untitled');
    setIsModified(false);
    return null; // caller should clear editor
  }, []);

  const openFile = useCallback(async () => {
    if (supportsFileSystemAccess) {
      try {
        const [handle] = await window.showOpenFilePicker({
          types: [RIPT_FILE_TYPE, TEXT_FILE_TYPE],
          multiple: false,
        });
        const file = await handle.getFile();
        const text = await file.text();

        if (file.name.endsWith('.ript')) {
          const data = JSON.parse(text);
          fileHandleRef.current = handle;
          setFileName(file.name);
          setIsModified(false);
          return { type: 'ript', content: data.content, mode: data?.metadata?.mode, multilingual: data?.metadata?.multilingual };
        } else {
          // Plain text — convert to TipTap-compatible content
          fileHandleRef.current = null;
          setFileName(file.name);
          setIsModified(false);
          return { type: 'text', content: text };
        }
      } catch (err) {
        if (err.name === 'AbortError') return null; // user cancelled
        throw err;
      }
    } else {
      // Fallback: hidden file input
      return new Promise((resolve) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.ript,.txt,.md';
        input.onchange = async (e) => {
          const file = e.target.files[0];
          if (!file) { resolve(null); return; }
          const text = await file.text();
          if (file.name.endsWith('.ript')) {
            const data = JSON.parse(text);
            setFileName(file.name);
            setIsModified(false);
            resolve({ type: 'ript', content: data.content, mode: data?.metadata?.mode, multilingual: data?.metadata?.multilingual });
          } else {
            setFileName(file.name);
            setIsModified(false);
            resolve({ type: 'text', content: text });
          }
        };
        input.click();
      });
    }
  }, []);

  const saveFile = useCallback(async (editorJson, mode, multilingual) => {
    const data = buildRiptData(editorJson, mode, multilingual);

    if (supportsFileSystemAccess && fileHandleRef.current) {
      await saveWithFSA(fileHandleRef.current, data);
      setIsModified(false);
      return true;
    }

    // No existing handle — fall through to saveAs
    return saveFileAs(editorJson, mode, multilingual);
  }, []);

  const saveFileAs = useCallback(async (editorJson, mode, multilingual) => {
    const data = buildRiptData(editorJson, mode, multilingual);

    if (supportsFileSystemAccess) {
      try {
        const handle = await window.showSaveFilePicker({
          suggestedName: fileName.endsWith('.ript') ? fileName : 'transcript.ript',
          types: [RIPT_FILE_TYPE],
        });
        await saveWithFSA(handle, data);
        fileHandleRef.current = handle;
        const file = await handle.getFile();
        setFileName(file.name);
        setIsModified(false);
        return true;
      } catch (err) {
        if (err.name === 'AbortError') return false;
        throw err;
      }
    } else {
      saveWithDownload(data, 'transcript.ript');
      setIsModified(false);
      return true;
    }
  }, [fileName]);

  const exportAsText = useCallback(async (plainText) => {
    if (supportsFileSystemAccess) {
      try {
        const handle = await window.showSaveFilePicker({
          suggestedName: 'transcript.txt',
          types: [TEXT_FILE_TYPE],
        });
        const writable = await handle.createWritable();
        await writable.write(plainText);
        await writable.close();
        return true;
      } catch (err) {
        if (err.name === 'AbortError') return false;
        throw err;
      }
    } else {
      const blob = new Blob([plainText], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'transcript.txt';
      a.click();
      URL.revokeObjectURL(url);
      return true;
    }
  }, []);

  const exportAsWord = useCallback(async (proseMirrorDoc, config) => {
    const docxDocument = serializeToDOCX(proseMirrorDoc, config ?? undefined);
    const blob = await Packer.toBlob(docxDocument);

    if (supportsFileSystemAccess) {
      try {
        const handle = await window.showSaveFilePicker({
          suggestedName: 'transcript.docx',
          types: [DOCX_FILE_TYPE],
        });
        const writable = await handle.createWritable();
        await writable.write(blob);
        await writable.close();
        return true;
      } catch (err) {
        if (err.name === 'AbortError') return false;
        throw err;
      }
    } else {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'transcript.docx';
      a.click();
      URL.revokeObjectURL(url);
      return true;
    }
  }, []);

  return {
    fileName,
    isModified,
    markModified,
    markSaved,
    newFile,
    openFile,
    saveFile,
    saveFileAs,
    exportAsText,
    exportAsWord,
  };
}
